import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { Typography, Legal, Asterisk } from '@figure/frontlib-gui';
import { numericFormatter } from '@figure/frontlib-util';
import { Card } from 'shared/components';
import { useCrossSell, useOffer, useProfile, useRedirect } from 'shared/hooks';
import { getLoanData } from 'shared/utils/offer-select-calculations';
import DesktopMultiProduct from './DesktopMultiProduct';
import MobileMultiProduct from './MobileMultiProduct';

const MultiProductCopy = styled(Typography.Body)`
  margin-bottom: 24px;
`;

const MultiProduct = () => {
  const history = useHistory();
  const { uuid: personalLoanUuid } = useParams();
  const { offerPackage, loading: offerLoading } = useOffer(true);
  const { profile, loading: profileLoading } = useProfile();
  const { crossSellOfferPackages, loading: crossSellLoading } = useCrossSell(true);
  const { redirectToLocation } = useRedirect();

  const isLoading = offerLoading || profileLoading || crossSellLoading;
  const helocOfferPackage = crossSellOfferPackages.find(
    (offerPackages) => offerPackages.productType === 'HELOC'
  );
  const plFixedOffers = offerPackage.offersByType?.find((ot) => ot.rateType === 'FIXED');

  // baseRate is the base interest rate on PL and needs to calc APR
  const lowestAprPl = plFixedOffers?.offers.reduce((aprRate, offer) => {
    const { apr } = getLoanData(
      offer.max,
      offer.baseRate,
      offer.term,
      offer.originationFeePercentage
    );

    if (apr < aprRate) return apr;

    return aprRate;
  }, 1);

  // intRate on the helocOfferPackage is APR already calc'd
  const lowestAprHeloc = helocOfferPackage?.offers.reduce(
    (aprRate, offer) => (offer.intRate < aprRate ? offer.intRate : aprRate),
    1
  );

  const personalLoanOfferInfo = {
    maxAmount: plFixedOffers?.max ?? 0,
    terms: plFixedOffers?.termsList ?? [],
    apr: lowestAprPl,
  };

  const helocOfferInfo = {
    maxAmount: helocOfferPackage?.max ?? 0,
    terms: helocOfferPackage?.termsList ?? [],
    apr: lowestAprHeloc,
  };

  const headerMaxAmount = [plFixedOffers?.max, helocOfferPackage?.max].reduce(
    (acc, cv) => (cv > acc ? cv : acc),
    0
  );

  const handlePersonalLoanClick = () =>
    history.push(`/applications/${personalLoanUuid}/offer-select`, {
      bypassCrossSellRedirect: true,
      plOfferSelected: true
    });
  const handleHelocClick = () =>
    redirectToLocation(
      `${process.env.REACT_APP_HELOC_PATH}/${helocOfferPackage.applicationUuid}/${
        helocOfferPackage.liensValidationRequired ? 'liens' : 'product/select'
      }`,
      true
    );

  if (isLoading) return null;

  return (
    <Card
      headerCopy={
        `Congrats${profile?.name?.firstName ? `, ${profile?.name?.firstName}` : ''}. 
         You’re pre-qualified to borrow up to ${numericFormatter.formatMoney(headerMaxAmount)}.`
      }
      customMaxWidth="84rem"
    >
      <MultiProductCopy>
        Good news. You are pre-qualified for both a personal loan and home equity line of credit
        (HELOC), which offers a lower starting rate and more cash to borrow. You can compare
        your options below and click to explore them in detail. You will be able to toggle
        between offers to choose the best product for you.
      </MultiProductCopy>

      <DesktopMultiProduct
        personalLoanOfferInfo={personalLoanOfferInfo}
        helocOfferInfo={helocOfferInfo}
        handlePersonalLoanClick={handlePersonalLoanClick}
        handleHelocClick={handleHelocClick}
      />

      <MobileMultiProduct
        personalLoanOfferInfo={personalLoanOfferInfo}
        helocOfferInfo={helocOfferInfo}
        handlePersonalLoanClick={handlePersonalLoanClick}
        handleHelocClick={handleHelocClick}
      />

      <Card.Footer>
        <Legal>
          <Asterisk />
          Five business day funding timeline assumes closing the loan with our remote online notary.
          Funding timelines may be longer for loans secured by properties located in counties that
          do not permit recording of e-signatures or that otherwise require an in-person closing.
        </Legal>

        <Legal>
          These offers are not commitments to lend. You may not qualify for the rates listed above.
          If approved for a Figure Personal Loan or Figure Home Equity Line, the fixed interest
          rates offered will depend on your creditworthiness, and are subject to our underwriting
          criteria, verification of your information and other terms and conditions.
        </Legal>

        <Legal>
          The Home Equity Line rate applies to the initial draw only. The rate that will apply to
          additional draws will be fixed at the time each additional draw is requested and will be
          based on a margin and index. The index is the highest domestic Prime Rate published in the
          Money Rates section of The Wall Street Journal (Eastern Edition) in the calendar month
          prior to the date the rate is fixed. The Figure Home Equity Line Agreement contains other
          important information about the loan.
        </Legal>

        <Legal>
          The Home Equity Line would be secured by your property and you could lose your home if you
          fail to make payments.
        </Legal>
      </Card.Footer>
    </Card>
  );
};

export default MultiProduct;
